import { OrderLineViewModel } from '@/@types/client-api';
import {
  ABTreatiseVariation,
  PublicationCatalogRelations,
  PublicationVariation,
  isABTreatiseContentType,
  useABTreatiseCatalogRelations
} from '@/hooks/PDP/usePublicationCatalogRelations';
import { mapToEcomVariant } from '@/hooks/PDP/usePublicationVariantSelection';
import { useProductContentQuery } from '@/hooks/PDP/useProductContentQuery';
import { useGetProductLinkQuery } from '@/redux/api/client/product';
import { useGetContentByFriendlyUrlQuery } from '@/redux/api/contentDeliveryAPI';
import { RootState } from '@/redux/store';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
  getCartProductDisplayType,
  getTreatiseCartProductDisplayType,
  shouldLookupTreatiseStpLabel
} from './labels';

type CartProductDisplayTypeProps = {
  item: OrderLineViewModel;
};

const getFriendlyUrl = (url?: string) => {
  if (!url) return undefined;

  let pathname: string;

  try {
    pathname = new URL(url, window.location.origin).pathname;
  } catch {
    return undefined;
  }

  return pathname.startsWith('/catalog/') ? pathname : undefined;
};

const mapCatalogVariants = (catalogRelations: PublicationCatalogRelations) => {
  const variants = [
    ...('printVariants' in catalogRelations ? (catalogRelations.printVariants ?? []) : []),
    catalogRelations.stsVariant
  ].filter(Boolean) as PublicationVariation[];
  const expiredPrintVariants = catalogRelations.expiredPrintVariants ?? [];

  return {
    variants: variants.map(variant => mapToEcomVariant(variant, catalogRelations, [])),
    expiredPrintVariants: expiredPrintVariants.map(variant =>
      mapToEcomVariant(variant, catalogRelations, [])
    )
  };
};

const CartProductDisplayType: React.FC<CartProductDisplayTypeProps> = ({ item }) => {
  const shouldLookupTreatise = shouldLookupTreatiseStpLabel(item);
  const fallbackDisplayType = getCartProductDisplayType(item);
  const pdpBox = useSelector((state: RootState) => state.pdp.box);
  const variants = pdpBox.catalogRelations?.variants ?? [];
  const expiredPrintVariants = pdpBox.catalogRelations?.expiredPrintVariants ?? [];
  const hasMatchingPdpVariant = variants.some(variant => variant.code === item.code);

  const shouldFetchTreatiseRelations = shouldLookupTreatise && !hasMatchingPdpVariant;
  const productContentQuery = useProductContentQuery();
  const productVariantUrl = getFriendlyUrl(item.productVariantUrl ?? undefined);
  const { data: productLink, isFetching: isProductLinkFetching } = useGetProductLinkQuery(
    item.code!,
    {
      skip: !shouldFetchTreatiseRelations || !!productVariantUrl || !item.code
    }
  );
  const friendlyUrl = productVariantUrl ?? getFriendlyUrl(productLink);
  const {
    data: content,
    isFetching: isContentFetching,
    isError: isContentError
  } = useGetContentByFriendlyUrlQuery(
    {
      friendlyUrl: friendlyUrl!,
      query: productContentQuery,
      userId: productContentQuery.userId ?? 'ANON'
    },
    { skip: !shouldFetchTreatiseRelations || !friendlyUrl }
  );
  const treatiseVariant =
    content && isABTreatiseContentType(content) ? (content as ABTreatiseVariation) : undefined;
  const treatiseCatalogRelations = useABTreatiseCatalogRelations(treatiseVariant);
  const isTreatiseRelationsLoading =
    shouldFetchTreatiseRelations &&
    (isProductLinkFetching || isContentFetching || treatiseCatalogRelations.isLoading);
  const fallbackTreatiseRelations = useMemo(
    () =>
      shouldFetchTreatiseRelations && treatiseVariant && !treatiseCatalogRelations.isError
        ? mapCatalogVariants(treatiseCatalogRelations)
        : undefined,
    [shouldFetchTreatiseRelations, treatiseCatalogRelations, treatiseVariant]
  );

  if (isTreatiseRelationsLoading) {
    return null;
  }

  const cartVariants = hasMatchingPdpVariant
    ? variants
    : (fallbackTreatiseRelations?.variants ?? []);
  const cartExpiredPrintVariants = hasMatchingPdpVariant
    ? expiredPrintVariants
    : (fallbackTreatiseRelations?.expiredPrintVariants ?? []);

  if (shouldLookupTreatise && (hasMatchingPdpVariant || fallbackTreatiseRelations)) {
    return (
      <>
        {getTreatiseCartProductDisplayType(
          item,
          cartVariants,
          cartExpiredPrintVariants,
          fallbackDisplayType
        )}
      </>
    );
  }

  if (shouldFetchTreatiseRelations && !friendlyUrl && !isProductLinkFetching) {
    return <>{fallbackDisplayType}</>;
  }

  if (shouldFetchTreatiseRelations && isContentError) {
    return <>{fallbackDisplayType}</>;
  }

  return <>{fallbackDisplayType}</>;
};

export default CartProductDisplayType;
